import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';

const prettyJson = (value) => {
  try {
    const obj = typeof value === 'string' ? JSON.parse(value || '{}') : (value ?? {});
    return JSON.stringify(obj, null, 2);
  } catch {
    return typeof value === 'string' ? value : '{}';
  }
};

// Shared Create / Edit modal. `mode` is 'create' | 'edit'.
const FeatureFlagModal = ({ show, mode, flag, onSave, onClose }) => {
  const isEdit = mode === 'edit';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [attributesText, setAttributesText] = useState('{}');
  const [error, setError] = useState('');

  // Reset fields whenever the modal opens (prefilled for edit, blank for create).
  useEffect(() => {
    if (!show) return;
    setName(flag?.name || '');
    setDescription(flag?.description || '');
    setAttributesText(isEdit ? prettyJson(flag?.attributes) : '{}');
    setError('');
  }, [show, flag, isEdit]);

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }

    // Validate JSON, but send it to the backend as a string (jsonb column).
    let parsed;
    try {
      parsed = attributesText.trim() ? JSON.parse(attributesText) : {};
    } catch {
      setError('Attributes must be valid JSON.');
      return;
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setError('Attributes must be a JSON object.');
      return;
    }

    onSave({
      name: trimmedName,
      description: description.trim(),
      attributes: JSON.stringify(parsed),
    });
  };

  return (
    <Modal show={show} onHide={onClose} centered dialogClassName="ff-modal" backdrop="static">
      <div className="ff-modal-header">
        <span className="ff-modal-title">
          {isEdit ? 'Edit Feature Flag' : 'Create Feature Flag'}
        </span>
        <button type="button" className="ff-modal-close" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>
      </div>

      <div className="ff-modal-body">
        <label className="ff-field-label">Name</label>
        <input
          type="text"
          className="ff-input"
          placeholder="e.g. dark_mode"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className="ff-field-label">Description</label>
        <input
          type="text"
          className="ff-input"
          placeholder="What does this flag control?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <label className="ff-field-label">
          Attributes <span className="ff-field-hint">(JSON object)</span>
        </label>
        <textarea
          className="ff-textarea ff-mono"
          rows={5}
          value={attributesText}
          onChange={(e) => setAttributesText(e.target.value)}
        />

        {error && <div className="ff-modal-error">{error}</div>}

        <div className="ff-modal-note">
          {isEdit ? (
            'Edits apply to the flag definition across all environments. Toggle states are not affected.'
          ) : (
            <>✓ This flag will be created in <strong>all environments</strong> and default to <strong>Off</strong> everywhere.</>
          )}
        </div>
      </div>

      <div className="ff-modal-footer">
        <button type="button" className="ff-btn ff-btn-outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="ff-btn ff-btn-primary" onClick={handleSave}>
          {isEdit ? 'Save Changes' : 'Create Flag'}
        </button>
      </div>
    </Modal>
  );
};

export default FeatureFlagModal;
